import { Box, Image, Theme, Typography } from "@outschool/backpack";
import { imageUrl } from "@outschool/filestack-urls";
import { Leader } from "@outschool/gql-backend-generated";
import {
  SearchListingsQuery,
  SaveButton_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import React from "react";

import { MAIN_IMAGE_HEIGHT, MAIN_IMAGE_WIDTH } from "./helpers";

type CardListing = SearchListingsQuery["searchListings"]["listings"][number];

type ListingProps = {
  listing: {
    activity:
      | Pick<
          CardListing["activity"],
          | "uid"
          | "details"
          | "age_min"
          | "age_max"
          | "weekly_meetings"
          | "duration_weeks"
          | "is_ongoing_weekly"
          | "isClub"
          | "isFlexSchedule"
          | "isOneTime"
          | "hasTeacherSchedule"
        > &
          SaveButton_ActivityFragment;
    leader: Pick<
      Leader,
      "name" | "reviewCount" | "photo" | "averageActivityStarRating"
    >;
    insertionId?: string;
  };
  width?: number;
};

export default function PhotoListing({ listing, width = 235 }: ListingProps) {
  const { t } = useTranslation(
    "ui-components-website\\src\\Listings\\PhotoListing",
  );
  const { translate } = useMachineTranslation();
  const imageHeight = (width / MAIN_IMAGE_WIDTH) * MAIN_IMAGE_HEIGHT;
  const photo = imageUrl(listing.activity.details.photo, {
    height: imageHeight,
    width,
    fit: "crop",
  });

  return (
    <Box
      sx={{
        width: width,
        height: imageHeight,
        borderRadius: "12px",
        position: "relative",
      }}
    >
      <Image
        sx={{
          width: width,
          height: imageHeight,
          borderRadius: "12px",
          objectFit: "cover",
          display: "block",
        }}
        src={photo}
        height={imageHeight}
        width={width}
        alt={t("Class listing photo")}
      />
      <Box
        sx={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          padding: ".5em",
          borderRadius: "0px 0px 12px 12px",
          background:
            "linear-gradient(180deg, rgba(0, 0, 0, 0.00) -20%, rgba(0, 0, 0, 0.82) 100%)",
        }}
      >
        <Typography
          variant="caption"
          sx={(theme: Theme) => ({
            color: theme.palette.common.white,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: "2",
            overflow: "hidden",
          })}
        >
          {translate(listing.activity.title)}
        </Typography>
      </Box>
    </Box>
  );
}
